import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { AlertTriangle, Download } from "lucide-react"
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts"
import AppLayout from "@/components/app/AppLayout"
import { FeatureGate } from "@/components/app/FeatureGate"
import { Button } from "@/components/ui/button"
import { API_BASE } from "@/config/api"
import { formatRon } from "@/lib/format"
import { useEntitlements } from "@/hooks/use-entitlements"
import { useClinicalContext } from "@/hooks/use-clinical-context"

const API = API_BASE
const PERIODS = [30, 90, 365]

function formatDelta(value, unit = "%") {
  if (value == null || Number.isNaN(value)) return null
  const sign = value > 0 ? "+" : ""
  return `${sign}${value}${unit}`
}

export default function Analytics() {
  const { t } = useTranslation()
  const { hasFeature } = useEntitlements()
  const { clinical } = useClinicalContext()
  const [days, setDays] = useState(90)
  const [data, setData] = useState(null)
  const [practitioners, setPractitioners] = useState(null)
  const [occupancy, setOccupancy] = useState(null)
  const [homecare, setHomecare] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const homecareOn = (clinical?.enabled_verticals || clinical?.effective_verticals || []).includes("home_care")
  const advancedOn = hasFeature("analytics_advanced")
  const adtOn = hasFeature("adt")

  const load = useCallback(() => {
    setLoading(true)
    setError(false)
    axios
      .get(`${API}/analytics/dashboard`, { params: { days } })
      .then((r) => {
        setData(r.data)
        setLoading(false)
      })
      .catch(() => {
        setData(null)
        setError(true)
        setLoading(false)
      })
  }, [days])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!advancedOn) {
      setPractitioners(null)
      return
    }
    axios
      .get(`${API}/analytics/practitioners`, { params: { days } })
      .then((r) => setPractitioners(r.data))
      .catch(() => setPractitioners(null))
  }, [advancedOn, days])

  useEffect(() => {
    if (!advancedOn || !adtOn) {
      setOccupancy(null)
      return
    }
    axios
      .get(`${API}/analytics/occupancy`, { params: { days } })
      .then((r) => setOccupancy(r.data))
      .catch(() => setOccupancy(null))
  }, [advancedOn, adtOn, days])

  useEffect(() => {
    if (!homecareOn) {
      setHomecare(null)
      return
    }
    const month = new Date().toISOString().slice(0, 7)
    axios
      .get(`${API}/analytics/homecare`, { params: { month } })
      .then((r) => setHomecare(r.data))
      .catch(() => setHomecare(null))
  }, [homecareOn])

  const exportCsv = () => {
    if (!data) return
    const rows = [
      ["metric", "value", "prior", "delta"],
      ["appointments", data.appointments_total, data.prior?.appointments_total, data.deltas?.appointments_pct],
      ["no_show_rate_pct", data.no_show_rate_pct, data.prior?.no_show_rate_pct, data.deltas?.no_show_rate_pp],
      ["utilization_pct", data.utilization_pct, data.prior?.utilization_pct, data.deltas?.utilization_pp],
      ["revenue_ron", data.revenue_collected_ron, data.prior?.revenue_collected_ron, data.deltas?.revenue_pct],
      ["outstanding_ron", data.outstanding_ron, "", ""],
      ["waitlist", data.waitlist_count, "", ""],
      [],
      ["series_week", "appointments", "no_shows", "revenue"],
      ...(data.series || []).map((s) => [s.week, s.appointments, s.no_shows, s.revenue]),
      [],
      ["aging_bucket", "amount_ron"],
      ...Object.entries(data.aging || {}).map(([k, v]) => [k, v]),
      [],
      ["waitlist_id", "client_id", "age_days"],
      ...(data.waitlist_aging || []).map((w) => [w.id, w.client_id, w.age_days]),
    ]
    const csv = rows.map((r) => r.join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `analytics-${days}d.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const cards = data
    ? [
        {
          key: "appointments",
          label: t("app.analytics.cards.appointments"),
          value: data.appointments_total,
          delta: formatDelta(data.deltas?.appointments_pct),
        },
        {
          key: "noShow",
          label: t("app.analytics.cards.noShow"),
          value: `${data.no_show_rate_pct}%`,
          delta: formatDelta(data.deltas?.no_show_rate_pp, "pp"),
        },
        {
          key: "utilization",
          label: t("app.analytics.cards.utilization"),
          value: `${data.utilization_pct}%`,
          delta: formatDelta(data.deltas?.utilization_pp, "pp"),
        },
        {
          key: "revenue",
          label: t("app.analytics.cards.revenue"),
          value: formatRon(data.revenue_collected_ron),
          delta: formatDelta(data.deltas?.revenue_pct),
        },
        {
          key: "outstanding",
          label: t("app.analytics.cards.outstanding"),
          value: formatRon(data.outstanding_ron),
          delta: null,
        },
        {
          key: "waitlist",
          label: t("app.analytics.cards.waitlist"),
          value: data.waitlist_count,
          delta: null,
        },
      ]
    : []

  const agingLabels = {
    current: t("app.analytics.aging.current"),
    "1_30": t("app.analytics.aging.d1_30"),
    "31_60": t("app.analytics.aging.d31_60"),
    "61_90": t("app.analytics.aging.d61_90"),
    "90_plus": t("app.analytics.aging.d90"),
  }

  const agingEntries = data?.aging
    ? ["current", "1_30", "31_60", "61_90", "90_plus"]
        .map((k) => ({ key: k, label: agingLabels[k], value: data.aging[k] || 0 }))
        .filter((e) => e.value > 0)
    : []

  const periodLabel = (d) => {
    if (d === 30) return t("app.analytics.days30")
    if (d === 90) return t("app.analytics.days90")
    return t("app.analytics.days365")
  }

  const anomalies = data?.anomalies || []

  return (
    <FeatureGate feature="analytics_basic">
      <AppLayout
        title={t("app.analytics.title")}
        actions={
          data ? (
            <Button variant="outline" size="sm" className="rounded-full" onClick={exportCsv}>
              <Download strokeWidth={1.5} className="h-4 w-4 mr-1" /> {t("app.analytics.exportCsv")}
            </Button>
          ) : null
        }
      >
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">
            {t("app.analytics.period")}
          </span>
          {PERIODS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`text-xs px-4 py-1.5 rounded-full transition-colors ${
                days === d
                  ? "bg-primary text-primary-foreground"
                  : "bg-card border border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {periodLabel(d)}
            </button>
          ))}
        </div>

        {loading && <p className="text-sm text-muted-foreground">{t("app.analytics.loading")}</p>}

        {error && !loading && (
          <div className="rounded-xl border border-border bg-card p-5 space-y-3 max-w-md">
            <p className="text-sm text-muted-foreground">{t("app.analytics.error")}</p>
            <Button variant="outline" size="sm" className="rounded-full" onClick={load}>
              {t("app.analytics.retry")}
            </Button>
          </div>
        )}

        {!loading && !error && data && (
          <div className="space-y-8 max-w-5xl">
            {anomalies.length > 0 && (
              <div className="space-y-2">
                {anomalies.map((a) => (
                  <div
                    key={a.id}
                    className={`rounded-xl border px-4 py-3 flex flex-wrap items-center gap-3 text-sm ${
                      a.severity === "danger"
                        ? "border-destructive/40 bg-destructive/10"
                        : a.severity === "warning"
                          ? "border-accent/40 bg-accent/10"
                          : "border-border bg-card"
                    }`}
                  >
                    <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                    <p className="flex-1">{a.message}</p>
                    {a.action_url && (
                      <Button asChild variant="outline" size="sm" className="rounded-full h-8">
                        <Link to={a.action_url}>{t("app.analytics.openAction")}</Link>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cards.map((c) => (
                <article key={c.key} className="rounded-xl border border-border bg-card p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">{c.label}</p>
                  <p className="font-display text-3xl tracking-tight mt-2">{c.value}</p>
                  {c.delta != null && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {c.delta} {t("app.analytics.vsPrior")}
                    </p>
                  )}
                </article>
              ))}
            </div>

            {(data.series || []).length > 0 ? (
              <div className="grid lg:grid-cols-2 gap-4">
                <section className="rounded-xl border border-border bg-card p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-4">
                    {t("app.analytics.charts.revenue")}
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={data.series}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={48} />
                      <Tooltip formatter={(v) => [formatRon(v), t("app.analytics.charts.revenueSeries")]} />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        name={t("app.analytics.charts.revenueSeries")}
                        stroke="hsl(var(--accent))"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </section>
                <section className="rounded-xl border border-border bg-card p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-4">
                    {t("app.analytics.charts.appointments")}
                  </p>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={data.series}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={36} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="appointments"
                        name={t("app.analytics.charts.apptSeries")}
                        stroke="hsl(var(--foreground))"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="no_shows"
                        name={t("app.analytics.charts.noShowSeries")}
                        stroke="hsl(var(--destructive))"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </section>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t("app.analytics.empty")}</p>
            )}

            <section className="rounded-xl border border-border bg-card p-5 space-y-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                {t("app.analytics.aging.title")}
              </p>
              {agingEntries.length ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {agingEntries.map((e) => (
                    <div key={e.key} className="rounded-lg bg-muted/40 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{e.label}</p>
                      <p className="font-medium mt-1">{formatRon(e.value)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("app.analytics.aging.empty")}</p>
              )}
            </section>

            <section className="rounded-xl border border-border bg-card p-5 space-y-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                {t("app.analytics.waitlist.title")}
              </p>
              {(data.waitlist_aging || []).length ? (
                <ul className="space-y-2 text-sm">
                  {data.waitlist_aging.slice(0, 10).map((w) => (
                    <li key={w.id} className="flex justify-between gap-4 border-b border-border/60 pb-2 last:border-0">
                      <span className="font-mono text-xs text-muted-foreground truncate">{w.client_id}</span>
                      <span className="text-muted-foreground shrink-0">
                        {t("app.analytics.waitlist.days", { days: w.age_days })}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">{t("app.analytics.waitlist.empty")}</p>
              )}
            </section>

            {advancedOn && practitioners?.practitioners?.length > 0 && (
              <section className="rounded-xl border border-border bg-card p-5 space-y-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t("app.analytics.practitioners.title")}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase text-muted-foreground">
                        <th className="text-left py-2">{t("app.analytics.practitioners.name")}</th>
                        <th className="text-right py-2">{t("app.analytics.practitioners.sessions")}</th>
                        <th className="text-right py-2">{t("app.analytics.practitioners.noShow")}</th>
                        <th className="text-right py-2">{t("app.analytics.practitioners.utilization")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {practitioners.practitioners.map((p) => (
                        <tr key={p.practitioner_user_id}>
                          <td className="py-2 font-medium">{p.name}</td>
                          <td className="py-2 text-right">{p.sessions}</td>
                          <td className="py-2 text-right">{p.no_show_rate_pct}%</td>
                          <td className="py-2 text-right">{p.utilization_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {advancedOn && adtOn && (
              <section className="rounded-xl border border-border bg-card p-5 space-y-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t("app.analytics.occupancy.title")}
                </p>
                {!occupancy?.wards?.length ? (
                  <p className="text-sm text-muted-foreground">{t("app.analytics.occupancy.empty")}</p>
                ) : (
                  <>
                    <div className="grid sm:grid-cols-4 gap-3">
                      <div className="rounded-lg bg-muted/40 px-3 py-2">
                        <p className="text-[10px] uppercase text-muted-foreground">{t("app.analytics.occupancy.total")}</p>
                        <p className="font-display text-2xl mt-1">{occupancy.summary?.total_beds ?? 0}</p>
                      </div>
                      <div className="rounded-lg bg-muted/40 px-3 py-2">
                        <p className="text-[10px] uppercase text-muted-foreground">{t("app.analytics.occupancy.occupied")}</p>
                        <p className="font-display text-2xl mt-1">{occupancy.summary?.occupied_beds ?? 0}</p>
                      </div>
                      <div className="rounded-lg bg-muted/40 px-3 py-2">
                        <p className="text-[10px] uppercase text-muted-foreground">{t("app.analytics.occupancy.available")}</p>
                        <p className="font-display text-2xl mt-1">{occupancy.summary?.available_beds ?? 0}</p>
                      </div>
                      <div className="rounded-lg bg-muted/40 px-3 py-2">
                        <p className="text-[10px] uppercase text-muted-foreground">{t("app.analytics.occupancy.rate")}</p>
                        <p className="font-display text-2xl mt-1">{occupancy.summary?.occupancy_pct ?? 0}%</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("app.analytics.occupancy.recent", {
                        admits: occupancy.recent?.admissions ?? 0,
                        discharges: occupancy.recent?.discharges ?? 0,
                        days: occupancy.period_days ?? days,
                      })}
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs uppercase text-muted-foreground">
                            <th className="text-left py-2">{t("app.analytics.occupancy.ward")}</th>
                            <th className="text-right py-2">{t("app.analytics.occupancy.occupied")}</th>
                            <th className="text-right py-2">{t("app.analytics.occupancy.total")}</th>
                            <th className="text-right py-2">{t("app.analytics.occupancy.rate")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {occupancy.wards.map((w) => (
                            <tr key={w.id}>
                              <td className="py-2 font-medium">
                                {w.name}
                                {w.active === false && (
                                  <span className="text-xs text-muted-foreground ml-2">
                                    ({t("app.adt.inactive")})
                                  </span>
                                )}
                              </td>
                              <td className="py-2 text-right">{w.occupied}</td>
                              <td className="py-2 text-right">{w.total}</td>
                              <td className="py-2 text-right">{w.occupancy_pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </section>
            )}

            {homecareOn && homecare && (
              <section className="rounded-xl border border-border bg-card p-5 space-y-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t("app.analytics.homecare.title")} ({homecare.month})
                </p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="rounded-lg bg-muted/40 px-3 py-2">
                    <p className="text-[10px] uppercase text-muted-foreground">{t("app.analytics.homecare.visits")}</p>
                    <p className="font-display text-2xl mt-1">{homecare.visits}</p>
                  </div>
                  <div className="rounded-lg bg-muted/40 px-3 py-2">
                    <p className="text-[10px] uppercase text-muted-foreground">{t("app.analytics.homecare.hours")}</p>
                    <p className="font-display text-2xl mt-1">{homecare.hours}</p>
                  </div>
                  <div className="rounded-lg bg-muted/40 px-3 py-2">
                    <p className="text-[10px] uppercase text-muted-foreground">{t("app.analytics.homecare.checkIn")}</p>
                    <p className="font-display text-2xl mt-1">{homecare.check_in_rate_pct}%</p>
                  </div>
                </div>
                {(homecare.missed_check_ins || []).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">{t("app.analytics.homecare.missed")}</p>
                    <ul className="text-sm space-y-1">
                      {homecare.missed_check_ins.slice(0, 8).map((m) => (
                        <li key={m.id} className="flex justify-between gap-2">
                          <span>{m.client_name}</span>
                          <span className="text-muted-foreground text-xs">{String(m.start).slice(0, 16)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(homecare.app_assignments || []).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">{t("app.analytics.homecare.appHours")}</p>
                    <ul className="text-sm space-y-1">
                      {homecare.app_assignments.map((a) => (
                        <li key={a.id} className="flex justify-between gap-2">
                          <span>{a.beneficiary_name}</span>
                          <span className="text-muted-foreground">
                            {a.hours_worked} / {a.hours_planned} h
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </AppLayout>
    </FeatureGate>
  )
}
